import type { BuyerSequenceFieldInfoDto } from "@/api/buyerSequence";
import { extractErrorMessage } from "@/api/client";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  SkeletonList,
  StatusBadge,
  SubTable,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { useWordingFields } from "./useWording";
import { conditionLabel, fieldTag } from "./templateTokens";

/**
 * The merge fields the wording may use — the same list the server validates a
 * save against and the Insert field menu offers — and the two conditions,
 * listed with their syntax because they are written, not inserted.
 */
export function FieldsReference() {
  const q = useWordingFields();

  const fieldColumns: SubTableColumn<BuyerSequenceFieldInfoDto>[] = [
    {
      key: "name",
      header: "Field",
      render: (f) => <code className="font-mono text-caption text-onyx">{fieldTag(f.name)}</code>,
      searchText: (f) => f.name,
    },
    { key: "label", header: "Label", render: (f) => f.label, searchText: (f) => f.label },
    {
      key: "fallback",
      header: "When there is no value",
      render: (f) =>
        f.required ? (
          <StatusBadge tone="warn">Required</StatusBadge>
        ) : f.fallback ? (
          <span>“{f.fallback}”</span>
        ) : (
          <span className="text-gray-mid">Left out</span>
        ),
      searchText: (f) => (f.required ? "Required" : (f.fallback ?? "")),
    },
    {
      key: "description",
      header: "What it is",
      render: (f) => <span className="text-caption text-onyx">{f.description}</span>,
      searchText: (f) => f.description,
    },
  ];

  return (
    <Card data-testid="wording-fields">
      <CardHeader>
        <CardTitle className="text-lead">Fields</CardTitle>
        <CardDescription>
          A required field with no value blocks copying the message; the others fall back
          to the words shown.
        </CardDescription>
      </CardHeader>

      {q.isLoading ? (
        <SkeletonList rows={3} />
      ) : q.isError ? (
        <div className="flex flex-wrap items-center gap-space-2" role="alert">
          <span className="text-small text-status-error">
            Couldn't load the field list. {extractErrorMessage(q.error)}
          </span>
          <Button size="sm" variant="secondary" onClick={() => void q.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-space-4">
          <SubTable
            caption="Merge fields"
            columns={fieldColumns}
            rows={(q.data ?? []).filter((f) => !f.isFlag)}
            rowKey={(f) => f.name}
            rowTestId={(f) => `wording-field-${f.name}`}
            pageSize={25}
            empty={{ message: "No merge fields are available." }}
          />

          <div className="flex flex-col gap-space-2" data-testid="wording-conditions">
            <h4 className="font-display text-small font-bold text-onyx">Conditions</h4>
            <p className="text-caption text-gray-mid">
              Wrap words in a condition to show them only in some messages. Type the syntax
              into the wording; each opening tag needs its closing tag.
            </p>
            {(q.data ?? [])
              .filter((f) => f.isFlag)
              .map((f) => (
                <div key={f.name} className="flex flex-col gap-space-1 rounded-md border border-border-light p-space-2">
                  <span className="text-small font-semibold text-onyx">{f.label}</span>
                  {f.description && (
                    <span className="text-caption text-onyx">{f.description}</span>
                  )}
                  <span className="text-caption text-onyx">
                    <code className="font-mono">{`{{#${f.name}}}…{{/${f.name}}}`}</code>{" "}
                    — shown {conditionLabel(f.name, false)}
                  </span>
                  <span className="text-caption text-onyx">
                    <code className="font-mono">{`{{^${f.name}}}…{{/${f.name}}}`}</code>{" "}
                    — shown {conditionLabel(f.name, true)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </Card>
  );
}
